import 'client-only';

import { AxiosError } from 'axios';

import { type PhotoUploadType } from '@/components/flow/steps/survey/surveyjsSurvey/surveyjs/components/photoUpload/consts';
import { type Brand } from '@/consts/brands';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { httpDelete, get, post } from '@/utils/http/client';
import { urls } from '@/utils/urls';

import { type FlowType } from '../funnelService/consts/flowType';
import { Loggingservice } from '../loggingService/client';

const PHOTO_UPLOAD_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes; uploads can be slow

export type Photo = {
  id: number;
  localUrl: string;
  metadata: {
    photo_perspective?: PhotoUploadType;
    treatment_category?: TreatmentCategory;
  };
};

const deletePhoto = (brand: Brand, photoId: number) =>
  httpDelete(urls[brand].api.photos.delete(photoId));

async function getPhotoTypes(
  brand: Brand,
  treatmentCategory: string,
  flowType?: FlowType,
) {
  try {
    const { data } = await get(
      urls[brand].api.photos.required(treatmentCategory),
      { params: { flowType } },
    );

    return data;
  } catch (exception) {
    console.error(exception);
  }
}

async function getUploadedPhotoData(brand: Brand) {
  try {
    const { data } = await get(urls[brand].api.photos.forFlows);

    return data;
  } catch (exception) {
    console.error(exception);
  }
}

async function uploadPhotos(
  brand: Brand,
  data: FormData,
): Promise<{
  photo?: Photo;
}> {
  const uploadStartTime = performance.now();
  const uploadStartISO = new Date().toISOString();

  try {
    const response = await post<{ photo?: Photo }>(
      urls[brand].api.photos.upload,
      data,
      { timeout: PHOTO_UPLOAD_TIMEOUT_MS },
    );
    return response.data;
  } catch (error: unknown) {
    const uploadFailTime = performance.now();
    const uploadFailISO = new Date().toISOString();
    const durationMs = uploadFailTime - uploadStartTime;

    let errorMessage = '';
    const errorData = { errorMessage: '', fileDetails: {} };

    if (error instanceof AxiosError) {
      errorData.errorMessage = error.response?.data.message;
      errorMessage = error.response?.data.message;
    } else {
      errorData.errorMessage = String(error);
      errorMessage = 'An unexpected error occurred while uploading photos.';
    }

    errorData.fileDetails = {
      ...getFileDetails(data),
      uploadStartISO,
      uploadFailISO,
      uploadDurationMs: Math.round(durationMs),
    };

    Loggingservice.sendLogsToDatadog(
      brand,
      '[Frontend] PhotoUpload ERROR',
      errorData,
      'error',
    );
    const uploadError = new Error(errorMessage);
    if (error instanceof AxiosError) {
      (uploadError as any).status = error.response?.status;
    }
    throw uploadError;
  }
}

const getFileDetails = (data: FormData) => {
  let fileDetails = {};
  const file = data.get('file');

  if (file instanceof File) {
    fileDetails = {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
    };
  }

  return fileDetails;
};

export const PhotoUploadService = {
  deletePhoto,
  getPhotoTypes,
  getUploadedPhotoData,
  uploadPhotos,
};
